import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './UsersListPreview.module.scss';
import cx from 'classnames';
import Avatar from '../Avatar/Avatar';
import { useTranslation } from 'react-i18next';
import useWindowSize from '../../hooks/useWindowSize';

const UsersListPreview = ({
  owner = null,
  user,
  users = [],
  maxShowAmount = 3,
  className,
  parentRef,
  maxLeftSpace = 0,
  widthChanged,
  size = 'small', // small, medium, large
}) => {
  const { t } = useTranslation();
  const usersListRef = useRef(null);
  const [showAmount, setShowAmount] = useState(maxShowAmount);

  const { width } = useWindowSize();

  const usersList = useMemo(() => {
    const usersArray = [];
    if (user) {
      usersArray.push({ ...user, avatar: user?.avatarUri ?? null });
    }
    if (owner?.username !== user?.username) {
      usersArray.push(owner);
    }
    if (users.length > 0) {
      usersArray.push(...users.filter(item => item.username !== user?.username));
    }
    return usersArray;
  }, [owner, user, users]);

  const getShowedAvatarsAmount = useCallback(() => {
    let showedAvatars = showAmount;
    if (usersList.length > showAmount) {
      showedAvatars += 1;
    }
    const pxPerAvatar = usersListRef.current.offsetWidth / showedAvatars;
    const newShowedAvatars = Math.trunc((parentRef?.current?.offsetWidth - maxLeftSpace) / pxPerAvatar);
    return usersList.length > showAmount ? Math.max(1, newShowedAvatars - 1) : newShowedAvatars;
  }, [showAmount, usersList]);

  useEffect(() => {
    if (parentRef?.current?.offsetWidth - usersListRef.current.offsetWidth < maxLeftSpace) {
      setShowAmount(getShowedAvatarsAmount());
    } else {
      const newAmount = getShowedAvatarsAmount();
      setShowAmount(Math.min(newAmount, maxShowAmount));
    }
  }, [width, maxLeftSpace, widthChanged]);

  return (
    <div className={cx(styles.container, className)} ref={usersListRef}>
      <>
        {usersList?.map((item, index, usersArr) => {
          if (index === 0 || (index === showAmount && usersArr.length === showAmount + 1)) {
            return (
              <Avatar
                key={index}
                image={item?.avatar}
                title={(item?.name ?? item?.userFullName) || t('folder.deleted_user')}
                className={styles.avatarStyle}
                size={size}
              />
            );
          }

          if (index < showAmount) {
            return (
              <Avatar
                key={index}
                image={item?.avatar}
                title={(item?.name ?? item?.userFullName) || t('folder.deleted_user')}
                className={styles.avatarStyle}
                size={size}
              />
            );
          }

          if (index === showAmount + 1) {
            return (
              <div key={index} className={cx(styles.item, styles[size])}>
                +{usersArr.length - showAmount}
              </div>
            );
          }

          return null;
        })}
      </>
    </div>
  );
};

export default UsersListPreview;
